import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, BookOpen, ArrowLeft, Lock, PlayCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClassDetail() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    loadClassData();
  }, [classId]);

  const loadClassData = async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}`);
      setClassData(res.data);
      // For now, grant access if class is free or to admin users
      setHasAccess(res.data.is_free || true); // TODO: Implement proper membership check
    } catch (error) {
      console.error('Failed to load class:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = (lessonId) => {
    if (classData.is_free || hasAccess) {
      navigate(`/lesson-player/${lessonId}`);
    } else {
      alert('This class requires a membership. Please subscribe to access lessons.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFA500] mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!classData) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-red-600">Class not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Button */}
      <div className="container mx-auto px-6 py-6">
        <button
          onClick={() => navigate('/class-center')}
          className="flex items-center gap-2 text-[#6B4423] hover:text-[#2C1810] font-bold"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Class Center
        </button>
      </div>

      {/* Class Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-12 mb-12">
        <div className="container mx-auto px-6">
          <div className="flex items-start gap-6">
            {/* Cover Image */}
            <div className="w-64 h-64 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              {classData.cover_image_url ? (
                <img 
                  src={classData.cover_image_url} 
                  alt={classData.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <GraduationCap className="w-32 h-32 text-white opacity-50" />
              )}
            </div>

            {/* Class Info */}
            <div className="flex-1">
              <h1 className="text-5xl font-bold retro-title mb-4">{classData.title}</h1>
              <p className="text-xl mb-6 leading-relaxed">{classData.description}</p>
              
              <div className="flex items-center gap-6 text-lg">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  <span>{classData.lessons?.length || 0} Lessons</span>
                </div>
                
                {classData.membership_required && !classData.is_free && (
                  <div className="flex items-center gap-2 bg-orange-500 px-4 py-2 rounded-full">
                    <Lock className="w-5 h-5" />
                    <span className="font-bold">Membership Required</span>
                  </div>
                )}
                
                {classData.is_free && (
                  <span className="bg-green-500 px-4 py-2 rounded-full font-bold">
                    FREE CLASS
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="container mx-auto px-6 pb-12">
        <h2 className="text-4xl font-bold text-[#2C1810] mb-8">Lessons</h2>
        
        {!classData.lessons || classData.lessons.length === 0 ? (
          <div className="bg-yellow-100 border-4 border-yellow-500 rounded-lg p-8 text-center">
            <p className="text-xl text-gray-700">
              No lessons available yet. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {classData.lessons.map((lesson, index) => (
              <div
                key={lesson.id}
                onClick={() => handleLessonClick(lesson.id)}
                className="bg-white border-4 border-[#8B5A3C] rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer transform hover:scale-[1.02]"
              >
                <div className="flex items-start gap-4">
                  {/* Lesson Number */}
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-white">{index + 1}</span>
                  </div>

                  {/* Lesson Info */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#2C1810] mb-2">{lesson.title}</h3>
                    <p className="text-gray-700 mb-3">{lesson.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        <span>{lesson.chapter_ids?.length || 0} Chapters</span>
                      </div>
                      
                      {lesson.status === 'published' && (
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                          Published
                        </span>
                      )}
                      
                      {lesson.status === 'draft' && (
                        <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full font-semibold">
                          Draft
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Play Icon */}
                  <div className="flex-shrink-0">
                    {(!classData.membership_required || classData.is_free || hasAccess) ? (
                      <PlayCircle className="w-12 h-12 text-emerald-600" />
                    ) : (
                      <Lock className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Access Prompt for Locked Classes */}
      {classData.membership_required && !classData.is_free && !hasAccess && (
        <div className="container mx-auto px-6 pb-12">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-8 rounded-lg text-center">
            <Lock className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">Membership Required</h3>
            <p className="text-xl mb-6">
              Subscribe to access all lessons in this class and more!
            </p>
            <button
              onClick={() => navigate('/class-center')}
              className="bg-white text-orange-600 px-8 py-3 rounded-lg font-bold text-xl hover:shadow-xl transition-all"
            >
              View Membership Options
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
